'use client';

import Link from 'next/link';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import { formatTimestamp } from '../../_utils';
import { AiDisclosure } from '../chat/AiDisclosure';
import { ChatPane } from '../chat/ChatPane';
import { UserMessageLayout } from '../chat/UserMessage';

const DEMO_ASSISTANT_RESPONSE = `I found several results matching your search. Here are some highlights:

**Top Results:**
1. **HD Stock Footage** — High-quality clips matching your description
2. **4K Cinematic Collection** — Premium footage with professional grading
3. **Editorial Picks** — Curated selections from our library

Would you like me to refine the search further?`;

const TYPING_SPEED_MS = 20;
const START_DELAY_MS = 1500;

interface DemoChatProps {
    query: string;
    onUpgradeClick: () => void;
}

/**
 * Demo chat pane for the visitor view.
 * Shows the visitor's actual query unblurred, with a blurred mock assistant
 * response that streams in. Includes "Upgrade to continue" and keyword search CTAs.
 */
export function DemoChat({ query, onUpgradeClick }: DemoChatProps): JSX.Element {
    const timestamp = useMemo(() => formatTimestamp(new Date()), []);
    const [revealedChars, setRevealedChars] = useState(0);

    useEffect(() => {
        if (revealedChars >= DEMO_ASSISTANT_RESPONSE.length) return;

        const delay = revealedChars === 0 ? START_DELAY_MS : TYPING_SPEED_MS;
        const timer = setTimeout(() => {
            setRevealedChars((prev) => prev + 1);
        }, delay);

        return (): void => clearTimeout(timer);
    }, [revealedChars]);

    const visibleText = DEMO_ASSISTANT_RESPONSE.slice(0, revealedChars);
    const isTyping = revealedChars < DEMO_ASSISTANT_RESPONSE.length;

    return (
        <ChatPane>
            <div className="flex h-full flex-col bg-white">
                <div className="flex-1 overflow-y-auto px-4 pt-2">
                    {/* User message — unblurred */}
                    <div className="py-3">
                        <UserMessageLayout timestamp={timestamp}>
                            <p>{query}</p>
                        </UserMessageLayout>
                    </div>

                    {/* Assistant response — blurred */}
                    {revealedChars > 0 && (
                        <div className="py-3 blur-sm select-none" aria-hidden="true">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-gray-900">Assistant</span>
                            </div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                {visibleText}
                                {isTyping && <span className="animate-pulse">|</span>}
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom section — unblurred */}
                <div className="sticky bottom-0 mt-auto bg-white p-4 space-y-3">
                    {/* Composer — visitors can type freely but submitting (Enter) triggers upgrade.
                        Intentionally no onClick redirect so the field feels interactive. */}
                    <div className="flex w-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-all focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                        <textarea
                            placeholder="Describe the scene you're looking for"
                            className="min-h-[80px] max-h-[200px] w-full resize-none overflow-y-auto bg-transparent px-3 py-2 text-sm outline-none placeholder:text-gray-400"
                            rows={3}
                            aria-label="Message input"
                            onKeyDown={(e): void => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    onUpgradeClick();
                                }
                            }}
                        />
                        {/* h-[30px] mirrors the button row height in ChatThread.tsx ThreadComposer */}
                        <div className="px-2 pb-2">
                            <div className="h-[30px]" />
                        </div>
                    </div>
                    <AiDisclosure />
                    {/* Upgrade CTA */}
                    <button
                        type="button"
                        onClick={onUpgradeClick}
                        className="w-full py-2.5 px-6 bg-yellow-400 hover:bg-yellow-600 text-gray-900 text-sm font-semibold rounded-lg cursor-pointer transition-colors"
                    >
                        Create an account to continue
                    </button>

                    {/* Keyword search link — visitor has no media-type signal, so default
                        to video keyword search without pre-filling their query. */}
                    <Link
                        href="/video/search"
                        className="flex items-center justify-center gap-2 py-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors drop-shadow-sm rounded-lg"
                    >
                        <Search className="w-3 h-3" />
                        Keyword search instead
                    </Link>
                </div>
            </div>
        </ChatPane>
    );
}
